/**
 * Generate initials from a full name
 * @param name - Full name (e.g., "Priya Sharma")
 * @returns Initials (e.g., "PS")
 */
export function getInitials(name: string): string {
  if (!name || name.trim().length === 0) {
    return '??';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {
    // Single name - take first 2 letters
    return parts[0].substring(0, 2).toUpperCase();
  }

  // Multiple names - take first letter of first and last name
  const firstInitial = parts[0].charAt(0).toUpperCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();

  return `${firstInitial}${lastInitial}`;
}

/**
 * Avatar color configuration
 * Each letter maps to a specific background and text color for consistency
 */
const AVATAR_COLORS: Record<string, { bg: string; text: string }> = {
  // Blue shades (A-E)
  A: { bg: '#dbeafe', text: '#1e40af' }, // blue-100, blue-800
  B: { bg: '#e0e7ff', text: '#3730a3' }, // indigo-100, indigo-800
  C: { bg: '#cffafe', text: '#155e75' }, // cyan-100, cyan-800
  D: { bg: '#d1fae5', text: '#065f46' }, // emerald-100, emerald-800
  E: { bg: '#dbeafe', text: '#1e40af' }, // blue-100, blue-800

  // Green shades (F-J)
  F: { bg: '#dcfce7', text: '#166534' }, // green-100, green-800
  G: { bg: '#d1fae5', text: '#065f46' }, // emerald-100, emerald-800
  H: { bg: '#ccfbf1', text: '#115e59' }, // teal-100, teal-800
  I: { bg: '#cffafe', text: '#155e75' }, // cyan-100, cyan-800
  J: { bg: '#dcfce7', text: '#166534' }, // green-100, green-800

  // Purple shades (K-O)
  K: { bg: '#ede9fe', text: '#5b21b6' }, // violet-100, violet-800
  L: { bg: '#f3e8ff', text: '#6b21a8' }, // purple-100, purple-800
  M: { bg: '#fae8ff', text: '#86198f' }, // fuchsia-100, fuchsia-800
  N: { bg: '#ede9fe', text: '#5b21b6' }, // violet-100, violet-800
  O: { bg: '#e0e7ff', text: '#3730a3' }, // indigo-100, indigo-800

  // Orange/Amber shades (P-T)
  P: { bg: '#ffedd5', text: '#9a3412' }, // orange-100, orange-800
  Q: { bg: '#fef3c7', text: '#92400e' }, // amber-100, amber-800
  R: { bg: '#fef9c3', text: '#854d0e' }, // yellow-100, yellow-800
  S: { bg: '#ffedd5', text: '#9a3412' }, // orange-100, orange-800
  T: { bg: '#fed7aa', text: '#c2410c' }, // orange-200, orange-700

  // Pink/Red shades (U-Z)
  U: { bg: '#fce7f3', text: '#9d174d' }, // pink-100, pink-800
  V: { bg: '#ffe4e6', text: '#9f1239' }, // rose-100, rose-800
  W: { bg: '#fee2e2', text: '#991b1b' }, // red-100, red-800
  X: { bg: '#fce7f3', text: '#9d174d' }, // pink-100, pink-800
  Y: { bg: '#fef3c7', text: '#92400e' }, // amber-100, amber-800
  Z: { bg: '#ffe4e6', text: '#9f1239' }, // rose-100, rose-800
};

// Default color for unknown characters
const DEFAULT_AVATAR_COLOR = { bg: '#f3f4f6', text: '#374151' }; // gray-100, gray-700

/**
 * Get avatar colors based on the first letter of a name
 * @param name - The name to get colors for
 * @returns Object with bg (background) and text color hex values
 */
export function getAvatarColors(name: string): { bg: string; text: string } {
  if (!name || name.trim().length === 0) {
    return DEFAULT_AVATAR_COLOR;
  }

  const firstLetter = name.trim().charAt(0).toUpperCase();
  return AVATAR_COLORS[firstLetter] || DEFAULT_AVATAR_COLOR;
}
